import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const bpKeys = {
    activeSeason: ['bp', 'active-season'],
    rewards: (sid) => ['bp', 'rewards', sid],
    missions: (sid) => ['bp', 'missions', sid],
    myPass: (sid, uid) => ['bp', 'my-pass', sid, uid],
    myMissions: (sid, uid) => ['bp', 'my-missions', sid, uid],
    myClaims: (sid, uid) => ['bp', 'my-claims', sid, uid],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
export function useActiveSeason() {
    return useQuery({
        queryKey: bpKeys.activeSeason,
        queryFn: async () => {
            const { data, error } = await supabase
                .from('seasons')
                .select('*')
                .eq('is_active', true)
                .maybeSingle();
            if (error)
                throw error;
            return data;
        },
        staleTime: 5 * 60_000,
    });
}
export function useSeasonRewards(seasonId) {
    return useQuery({
        queryKey: seasonId ? bpKeys.rewards(seasonId) : ['bp', 'rewards', 'none'],
        enabled: !!seasonId,
        queryFn: async () => {
            if (!seasonId)
                return [];
            const { data, error } = await supabase
                .from('season_pass_rewards')
                .select('*')
                .eq('season_id', seasonId)
                .order('level', { ascending: true });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 5 * 60_000,
    });
}
export function useSeasonMissions(seasonId) {
    return useQuery({
        queryKey: seasonId ? bpKeys.missions(seasonId) : ['bp', 'missions', 'none'],
        enabled: !!seasonId,
        queryFn: async () => {
            if (!seasonId)
                return [];
            const { data, error } = await supabase
                .from('season_missions')
                .select('*')
                .eq('season_id', seasonId)
                .order('type', { ascending: true })
                .order('name', { ascending: true });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 5 * 60_000,
    });
}
export function useMyPass(seasonId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: seasonId && user?.id ? bpKeys.myPass(seasonId, user.id) : ['bp', 'my-pass', 'none'],
        enabled: !!seasonId && !!user?.id,
        queryFn: async () => {
            if (!seasonId || !user?.id)
                return null;
            const { data, error } = await supabase
                .from('player_season_pass')
                .select('*')
                .eq('season_id', seasonId)
                .eq('player_id', user.id)
                .maybeSingle();
            if (error)
                throw error;
            return (data ?? null);
        },
        staleTime: 30_000,
    });
}
export function useMyMissions(seasonId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: seasonId && user?.id ? bpKeys.myMissions(seasonId, user.id) : ['bp', 'my-missions', 'none'],
        enabled: !!seasonId && !!user?.id,
        queryFn: async () => {
            if (!seasonId || !user?.id)
                return [];
            const { data, error } = await supabase
                .from('player_missions')
                .select('*')
                .eq('season_id', seasonId)
                .eq('player_id', user.id);
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 30_000,
    });
}
/** Set des (level, track) déjà claimés par le joueur. */
export function useMyClaims(seasonId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: seasonId && user?.id ? bpKeys.myClaims(seasonId, user.id) : ['bp', 'my-claims', 'none'],
        enabled: !!seasonId && !!user?.id,
        queryFn: async () => {
            if (!seasonId || !user?.id)
                return new Set();
            const { data, error } = await supabase
                .from('player_pass_claims')
                .select('level, track')
                .eq('season_id', seasonId)
                .eq('player_id', user.id);
            if (error)
                throw error;
            const set = new Set();
            for (const row of (data ?? [])) {
                set.add(`${row.level}|${row.track}`);
            }
            return set;
        },
        staleTime: 30_000,
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Mutations
// ────────────────────────────────────────────────────────────────────────────
function invalidatePass(qc, seasonId, uid) {
    if (!seasonId || !uid)
        return;
    void qc.invalidateQueries({ queryKey: bpKeys.myPass(seasonId, uid) });
    void qc.invalidateQueries({ queryKey: bpKeys.myMissions(seasonId, uid) });
    void qc.invalidateQueries({ queryKey: bpKeys.myClaims(seasonId, uid) });
}
export function useClaimReward() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (input) => {
            const { data, error } = await supabase.rpc('claim_pass_reward', {
                p_season_id: input.seasonId,
                p_level: input.level,
                p_track: input.track,
            });
            if (error)
                throw error;
            return data;
        },
        onSuccess: (_d, vars) => invalidatePass(qc, vars.seasonId, user?.id),
    });
}
export function useClaimMission() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (missionId) => {
            const { data, error } = await supabase.rpc('claim_mission', { p_mission_id: missionId });
            if (error)
                throw error;
            const row = Array.isArray(data) ? data[0] : data;
            if (!row)
                throw new Error('claim_mission returned no row');
            return {
                bpxp_awarded: Number(row.bpxp_awarded),
                pass_level: Number(row.pass_level),
                pass_bpxp: Number(row.pass_bpxp),
            };
        },
        onSuccess: () => {
            // Invalidation large : on ne connaît pas la season à ce niveau.
            void qc.invalidateQueries({ queryKey: ['bp'] });
            if (user?.id)
                void qc.invalidateQueries({ queryKey: ['profile', user.id] });
        },
    });
}
export function useCheckMyMissions() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.rpc('check_my_missions');
            if (error)
                throw error;
            return (data ?? []);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['bp'] });
        },
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
export function bpProgress(pass, season) {
    if (!pass || !season) {
        return { level: 0, current: 0, perLevel: season?.bpxp_per_level ?? 1000, percent: 0, max: season?.bp_levels_count ?? 50 };
    }
    const perLevel = season.bpxp_per_level;
    const inLevel = Number(pass.current_bpxp) % perLevel;
    return {
        level: pass.current_level,
        current: inLevel,
        perLevel,
        percent: (inLevel / perLevel) * 100,
        max: season.bp_levels_count,
    };
}
export function daysRemaining(season) {
    if (!season)
        return 0;
    const end = new Date(season.end_date).getTime();
    const now = Date.now();
    const days = Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    return days;
}
